import axios, { AxiosInstance } from "axios";
import constant from "enum/constant";
import { toast } from "react-toastify";

const { API_URL, API_BASE_URL, API_HEADER_KEY, API_HEADER_VALUE, PYTHON_API_URL } = constant;

const axiosClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

export const axiosClientPyhton = axios.create({
  baseURL: PYTHON_API_URL,
});

export const axiosClientBase = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const setupInterceptors = (client: AxiosInstance, extraHeadersProvider: () => Record<string, string> = () => ({})) => {
  client.interceptors.request.use(
    (config: any) => {
      const token = sessionStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = "Bearer " + token;
      }

      const extraHeaders = extraHeadersProvider();
      Object.entries(extraHeaders).forEach(([key, value]) => {
        config.headers[key] = value;
      });

      // Automatically sanitize params to strip missing query parameters
      if (config.params) {
        const cleanedParams: Record<string, any> = {};
        for (const key in config.params) {
          const val = config.params[key];
          if (
            val !== undefined &&
            val !== null &&
            val !== "" &&
            !(typeof val === "string" && val.trim() === "")
          ) {
            cleanedParams[key] = val;
          }
        }
        config.params = cleanedParams;
      }

      return config;
    },
    (error: any) => {
      toast.error("Token is required.");
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      if (error.response?.status === 403 && !error.config?.skipAuthRedirect) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/auth/login";
      }
      return Promise.reject(error);
    }
  );
};

setupInterceptors(axiosClient, () => ({
  [`${API_HEADER_KEY}`]: `${API_HEADER_VALUE}`,
}));

setupInterceptors(axiosClientPyhton, () => ({
  [API_HEADER_KEY]: API_HEADER_VALUE,
}));

setupInterceptors(axiosClientBase, () => ({
  "x-api-key": process.env.REACT_APP_INQUIRY_API_KEY ?? "",
  "origin": process.env.REACT_APP_INQUIRY_ORIGIN ?? "",
}));

// Utility to clean payloads of undefined/null fields commonly needed in PUT/POST 
export const cleanPayload = (payload: any) => {
  if (typeof payload !== "object" || payload === null) return payload;
  const newPayload = { ...payload };
  for (const key in newPayload) {
    if (newPayload[key] === undefined || newPayload[key] === null) {
      delete newPayload[key];
    }
  }
  return newPayload;
};

export default axiosClient;
